import React, { useState, useEffect, useCallback } from 'react';
import { listarCampanias } from '../../services/campanias';
import { listarContenidoPorCampania, eliminarContenido, generarImagen } from '../../services/contenido';
import '../../styles/admin/AdminContenido.css';

function AdminContenido() {
  const [campanias, setCampanias] = useState([]);
  const [campaniaSeleccionada, setCampaniaSeleccionada] = useState(null);
  const [contenidos, setContenidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generando, setGenerando] = useState(false);
  const [mensajeExito, setMensajeExito] = useState('');
  
  // Estado para el modal de imagen
  const [imagenModal, setImagenModal] = useState(null);
  
  const [tipoGeneracion, setTipoGeneracion] = useState('pdf_solo');
  
  const [opciones, setOpciones] = useState({
    aspect_ratio: '16:9',
    tono: 'profesional',
    estilo_visual: 'minimalista',
    colores_marca: [],
    objetivo: 'branding',
    recomendaciones_extra: ''
  });
  
  const [imagenesReferencia, setImagenesReferencia] = useState([]);

  // Colores dinámicos
  const agregarColor = () => {
    if (opciones.colores_marca.length < 3) {
      setOpciones({
        ...opciones,
        colores_marca: [...opciones.colores_marca, '#272765']
      });
    }
  };

  const eliminarColor = (index) => {
    const nuevosColores = opciones.colores_marca.filter((_, i) => i !== index);
    setOpciones({
      ...opciones,
      colores_marca: nuevosColores
    });
  };

  const actualizarColor = (index, nuevoColor) => {
    const nuevosColores = [...opciones.colores_marca];
    nuevosColores[index] = nuevoColor;
    setOpciones({
      ...opciones,
      colores_marca: nuevosColores
    });
  };

  const handleImagenReferenciaChange = (file, tipo) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const nuevaImagen = {
        id: Date.now() + Math.random(),
        tipo: tipo,
        archivo: file,
        base64: reader.result,
        nombre: file.name
      };
      
      setImagenesReferencia(prev => {
        const filtradas = prev.filter(img => img.tipo !== tipo);
        return [...filtradas, nuevaImagen];
      });
    };
    reader.readAsDataURL(file);
  };

  const eliminarImagenReferencia = (id) => {
    setImagenesReferencia(prev => prev.filter(img => img.id !== id));
  };

  const cargarCampanias = useCallback(async () => {
    try {
      const data = await listarCampanias();
      setCampanias(data);
      if (data.length > 0) setCampaniaSeleccionada(data[0]);
    } catch (err) {
      setError('No se pudieron cargar las campañas');
    }
  }, []);

  const cargarContenido = useCallback(async () => {
    if (!campaniaSeleccionada) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await listarContenidoPorCampania(campaniaSeleccionada.id);
      setContenidos(data);
    } catch (err) {
      setError('Error al cargar el contenido');
    } finally {
      setLoading(false); 
    }
  }, [campaniaSeleccionada]);

  useEffect(() => {
    cargarCampanias();
  }, []);

  useEffect(() => {
    if (campaniaSeleccionada) {
      cargarContenido();
    } else {
      setLoading(false); 
    }
  }, [campaniaSeleccionada]);

  // 🔥 Calcular días restantes para expiración
  const calcularDiasRestantes = (fechaGeneracion) => {
    const fecha = new Date(fechaGeneracion);
    const expiracion = new Date(fecha.getTime() + 7 * 24 * 60 * 60 * 1000);
    const ahora = new Date();
    const diff = expiracion - ahora;
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return dias;
  };

  // 🔥 Verificar si el contenido ha caducado
  const haCaducado = (fechaGeneracion) => {
    const dias = calcularDiasRestantes(fechaGeneracion);
    return dias <= 0;
  };

  const handleGenerarContenido = async () => {
    if (!campaniaSeleccionada) {
      setError('Selecciona una campaña primero');
      return;
    }
    
    const esConReferencias = tipoGeneracion === 'pdf_referencias';
    
    if (esConReferencias && imagenesReferencia.length === 0) {
      setError('Debes subir al menos una imagen de referencia (logo, publicidad o producto)');
      return;
    }
    
    setGenerando(true);
    setError('');
    setMensajeExito('');
    
    try {
      let imagenesParaEnviar = null;
      if (esConReferencias) {
        imagenesParaEnviar = imagenesReferencia.map(img => ({
          tipo: img.tipo,
          imagen_base64: img.base64,
          descripcion: img.nombre || `${img.tipo}`
        }));
      }
      
      const baseData = {
        campania_id: campaniaSeleccionada.id,
        aspect_ratio: opciones.aspect_ratio,
        tono: opciones.tono,
        objetivo: opciones.objetivo,
        recomendaciones_extra: opciones.recomendaciones_extra,
        imagenes_referencia: imagenesParaEnviar
      };
      
      if (!esConReferencias) {
        const logoImg = imagenesReferencia.find(img => img.tipo === 'logo');
        if (logoImg) {
          baseData.logo_base64 = logoImg.base64;
        }
      }
      
      if (esConReferencias) {
        baseData.colores_marca = opciones.colores_marca.length > 0 
          ? opciones.colores_marca.join(',') 
          : null;
        baseData.estilo_visual = opciones.estilo_visual;
      } else {
        baseData.colores_marca = opciones.colores_marca.length > 0 
          ? opciones.colores_marca.join(',') 
          : null;
        baseData.estilo_visual = opciones.estilo_visual;
      }
      
      const resultado = await generarImagen(baseData);
      
      const creditosConsumidos = esConReferencias ? 3 : 1;
      setMensajeExito(
        `✅ ${esConReferencias ? 'Imagen con referencias' : 'Imagen desde PDF'} generada! ` +
        `Se consumieron ${creditosConsumidos} crédito(s). ` +
        `Créditos restantes: ${resultado.creditos_restantes}`
      );
      
      cargarContenido();
      
      // 🔥 Limpiar imágenes de referencia
      setImagenesReferencia([]);
      
      setTimeout(() => setMensajeExito(''), 5000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerando(false);
    }
  };

  const handleDescargarImagen = async (url, nombre) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${nombre}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error descargando imagen:', error);
      setError('No se pudo descargar la imagen');
    }
  };

  const eliminarContenidoHandler = async (id) => {
    if (window.confirm('¿Eliminar este contenido?')) {
      try {
        await eliminarContenido(id);
        cargarContenido();
      } catch (err) {
        setError('Error al eliminar el contenido');
      }
    }
  };

  const creditosNecesarios = tipoGeneracion === 'pdf_referencias' ? 3 : 1;
  const labelTipo = tipoGeneracion === 'pdf_referencias' 
    ? 'PDF + Referencias' 
    : 'PDF solo';

  return (
    <div className="admin-contenido-layout">
      {/* MODAL PARA VER IMAGEN COMPLETA */}
      {imagenModal && (
        <div className="modal-overlay" onClick={() => setImagenModal(null)}>
          <div className="modal-content-imagen" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-imagen" onClick={() => setImagenModal(null)}>✕</button>
            <img src={imagenModal} alt="Imagen ampliada" className="modal-imagen" />
          </div>
        </div>
      )}

      {/* COLUMNA IZQUIERDA - FORMULARIO */}
      <div className="form-col">
        <div className="form-card">
          <h2>🎨 Generar contenido (Admin - Sin límites)</h2>
          
          {mensajeExito && <div className="mensaje-exito">{mensajeExito}</div>}
          
          <div className="form-group">
            <label>Campaña</label>
            <select
              value={campaniaSeleccionada?.id || ''}
              onChange={(e) => {
                const selected = campanias.find(c => c.id === parseInt(e.target.value));
                setCampaniaSeleccionada(selected);
              }}
            >
              {campanias.map(c => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Tipo de generación</label>
            <div className="tipo-selector">
              <button
                type="button"
                className={`tipo-btn ${tipoGeneracion === 'pdf_solo' ? 'active' : ''}`}
                onClick={() => setTipoGeneracion('pdf_solo')}
              >
                📄 PDF solo (1 crédito)
              </button>
              <button
                type="button"
                className={`tipo-btn ${tipoGeneracion === 'pdf_referencias' ? 'active' : ''}`}
                onClick={() => setTipoGeneracion('pdf_referencias')}
              >
                🖼️ PDF + Referencias (3 créditos)
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Formato</label>
            <select value={opciones.aspect_ratio} onChange={(e) => setOpciones({...opciones, aspect_ratio: e.target.value})}>
              <option value="1:1">Cuadrado (1:1) - Instagram feed</option>
              <option value="4:5">Vertical (4:5) - Instagram/ Facebook</option>
              <option value="16:9">Horizontal (16:9) - Web / YouTube</option>
              <option value="9:16">Historia (9:16) - Stories / TikTok / Reels</option>
            </select>
          </div>

          <div className="form-group">
            <label>Tono</label>
            <select value={opciones.tono} onChange={(e) => setOpciones({...opciones, tono: e.target.value})}>
              <option value="profesional">Profesional</option>
              <option value="divertido">Divertido</option>
              <option value="emocional">Emocional</option>
              <option value="juvenil">Juvenil</option>
              <option value="inspirador">Inspirador</option>
            </select>
          </div>

          <div className="form-group">
            <label>Objetivo</label>
            <select value={opciones.objetivo} onChange={(e) => setOpciones({...opciones, objetivo: e.target.value})}>
              <option value="ventas">Ventas</option>
              <option value="branding">Branding</option>
              <option value="engagement">Engagement</option>
              <option value="leads">Leads</option>
              <option value="trafico">Tráfico</option>
            </select>
          </div>

          <div className="form-group">
            <label>Estilo visual</label>
            <select value={opciones.estilo_visual} onChange={(e) => setOpciones({...opciones, estilo_visual: e.target.value})}>
              <option value="minimalista">Minimalista</option>
              <option value="cinematic">Cinematográfico</option>
              <option value="flat">Plano</option>
              <option value="3d">3D</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>

          <div className="form-group color-section">
            <div className="color-header">
              <label>Colores de marca (opcional)</label>
              {opciones.colores_marca.length < 3 && (
                <button type="button" className="btn-agregar-color" onClick={agregarColor}>
                  ➕ Agregar color
                </button>
              )}
            </div>
            <div className="color-pickers">
              {opciones.colores_marca.map((color, index) => (
                <div key={index} className="color-item">
                  <label>Color {index + 1}</label>
                  <div className="color-input-wrapper">
                    <input 
                      type="color" 
                      value={color} 
                      onChange={(e) => actualizarColor(index, e.target.value)} 
                    />
                    <button 
                      type="button" 
                      className="btn-eliminar-color"
                      onClick={() => eliminarColor(index)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {opciones.colores_marca.length === 0 && (
              <small className="color-hint">Haz clic en "Agregar color" para añadir hasta 3 colores.</small>
            )}
            {opciones.colores_marca.length > 0 && (
              <small>Colores seleccionados: {opciones.colores_marca.length}/3</small>
            )}
          </div>

          {tipoGeneracion === 'pdf_referencias' && (
            <div className="form-group referencias-section">
              <label>Imágenes de referencia (hasta 14, opcionales)</label>
              <div className="referencias-grid">
                <div className="referencia-item">
                  <label>Logo</label>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png" 
                    onChange={(e) => handleImagenReferenciaChange(e.target.files[0], 'logo')}
                  />
                  {imagenesReferencia.find(img => img.tipo === 'logo') && (
                    <div className="referencia-preview">
                      <img src={imagenesReferencia.find(img => img.tipo === 'logo').base64} alt="Logo" />
                      <button onClick={() => eliminarImagenReferencia(imagenesReferencia.find(img => img.tipo === 'logo').id)}>✕</button>
                    </div>
                  )}
                </div>
                
                <div className="referencia-item">
                  <label>Publicidad referencia</label>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png" 
                    onChange={(e) => handleImagenReferenciaChange(e.target.files[0], 'referencia')}
                  />
                  {imagenesReferencia.find(img => img.tipo === 'referencia') && (
                    <div className="referencia-preview">
                      <img src={imagenesReferencia.find(img => img.tipo === 'referencia').base64} alt="Referencia" />
                      <button onClick={() => eliminarImagenReferencia(imagenesReferencia.find(img => img.tipo === 'referencia').id)}>✕</button>
                    </div>
                  )}
                </div>
                
                <div className="referencia-item">
                  <label>Producto</label>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png" 
                    onChange={(e) => handleImagenReferenciaChange(e.target.files[0], 'producto')}
                  />
                  {imagenesReferencia.find(img => img.tipo === 'producto') && (
                    <div className="referencia-preview">
                      <img src={imagenesReferencia.find(img => img.tipo === 'producto').base64} alt="Producto" />
                      <button onClick={() => eliminarImagenReferencia(imagenesReferencia.find(img => img.tipo === 'producto').id)}>✕</button>
                    </div>
                  )}
                </div>
              </div>
              <small className="referencias-hint">
                Sube hasta 3 imágenes: Logo, Publicidad referencia y Producto. 
                Todas son opcionales pero mejoran el resultado.
              </small>
            </div>
          )}

          <div className="form-group">
            <label>Recomendaciones extra</label>
            <textarea 
              rows="3" 
              placeholder="Describe algo específico que quieras en el contenido..." 
              value={opciones.recomendaciones_extra} 
              onChange={(e) => setOpciones({...opciones, recomendaciones_extra: e.target.value})} 
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <button 
            className="btn-generar-imagen" 
            onClick={handleGenerarContenido} 
            disabled={generando}
          >
            {generando ? (
              <>
                <span className="spinner-small"></span>
                Generando {labelTipo}...
              </>
            ) : (
              `🎨 ${labelTipo} (${creditosNecesarios} crédito${creditosNecesarios > 1 ? 's' : ''})`
            )}
          </button>
        </div>
      </div>

      {/* COLUMNA DERECHA - GALERÍA */}
      <div className="galeria-col">
        <div className="galeria-card">
          <h2>🖼️ Contenido generado</h2>
          
          {loading ? (
            <div className="loading">Cargando contenido...</div>
          ) : contenidos.length === 0 ? (
            <div className="sin-imagenes">
              <p>No hay contenido generado para esta campaña.</p>
              <p>Usa el formulario para crear tu primer contenido con IA.</p>
            </div>
          ) : (
            <div className="imagenes-grid">
              {contenidos
                .filter(item => {
                  if (!item.fecha_generacion) return true;
                  return !haCaducado(item.fecha_generacion);
                })
                .map((item) => {
                  const diasRestantes = item.fecha_generacion 
                    ? calcularDiasRestantes(item.fecha_generacion) 
                    : null;
                  
                  return (
                    <div key={item.id} className="imagen-card">
                      {item.url_imagen && (
                        <>
                          <img 
                            src={item.url_imagen} 
                            alt={item.copy_post || 'Imagen generada'} 
                            onClick={() => setImagenModal(item.url_imagen)}
                            style={{ cursor: 'pointer' }}
                          />
                          {diasRestantes !== null && diasRestantes > 0 && (
                            <div className="fecha-expiracion">
                              ⏳ Expira en {diasRestantes} día{diasRestantes > 1 ? 's' : ''}
                            </div>
                          )}
                          {diasRestantes !== null && diasRestantes <= 0 && (
                            <div className="fecha-expiracion caducado">
                              ❌ Contenido caducado
                            </div>
                          )}
                        </>
                      )}
                      <div className="imagen-info">
                        {item.copy_post && <p className="imagen-copy">{item.copy_post}</p>}
                        {item.copy_descripcion && <p className="imagen-desc">{item.copy_descripcion}</p>}
                        {item.hashtags && <p className="imagen-hashtags">{item.hashtags}</p>}
                        <div className="imagen-acciones">
                          {item.url_imagen && (
                            <button 
                              className="btn-descargar" 
                              onClick={() => handleDescargarImagen(item.url_imagen, `imagen_${item.id}`)}
                            >
                              📥 Descargar
                            </button>
                          )}
                          <button 
                            className="btn-eliminar" 
                            onClick={() => eliminarContenidoHandler(item.id)}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminContenido;